import { isGuidePath } from './userGuide.js';
import { dotSegments, linkCardApp } from './linkCardApps.js';
import { petChatNavigation } from './pets/chatNavigation.js';
const MAX_DEPTH = 20;
const ESCAPABLE = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/;
/** Absolute web/email links and strict guide paths; the renderer requires a configured service for guide paths. */
export function safeMarkdownUrl(raw) {
    const value = raw.replace(/\\([!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~])/g, '$1');
    if (!value || /[\u0000-\u0020\u007f]/.test(value))
        return null;
    if (isGuidePath(value))
        return value;
    if (petChatNavigation(value))
        return value;
    try {
        const url = new URL(value);
        if (!['https:', 'http:', 'mailto:'].includes(url.protocol) || url.username || url.password)
            return null;
        return value;
    }
    catch {
        return null;
    }
}
function codeEnd(text, from, length) {
    for (let i = from; i < text.length; i++) {
        if (text[i] !== '`')
            continue;
        let end = i + 1;
        while (text[end] === '`')
            end++;
        if (end - i === length)
            return i;
        i = end - 1;
    }
    return -1;
}
/** Split only structural pipes; preserve escaped pipes and pipes inside matched code spans. */
export function splitTableRow(line) {
    const cells = [];
    let cell = '';
    let pipe = false;
    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '\\' && i + 1 < line.length) {
            cell += char + line[++i];
            continue;
        }
        if (char === '`') {
            let run = i + 1;
            while (line[run] === '`')
                run++;
            const close = codeEnd(line, run, run - i);
            if (close >= 0) {
                cell += line.slice(i, close + run - i);
                i = close + run - i - 1;
                continue;
            }
        }
        if (char === '|') {
            cells.push(cell.trim());
            cell = '';
            pipe = true;
        }
        else
            cell += char;
    }
    if (!pipe)
        return null;
    cells.push(cell.trim());
    if (cells[0] === '')
        cells.shift();
    if (cells.at(-1) === '')
        cells.pop();
    return cells;
}
function linkAt(text, offset) {
    let depth = 1;
    let i = offset + 1;
    for (; i < text.length; i++) {
        if (i - offset > 4096 || depth > MAX_DEPTH)
            return null;
        if (text[i] === '\\') {
            i++;
            continue;
        }
        if (text[i] === '[')
            depth++;
        else if (text[i] === ']' && --depth === 0)
            break;
    }
    if (depth !== 0 || text[i + 1] !== '(')
        return null;
    const label = text.slice(offset + 1, i);
    const start = i + 2;
    depth = 1;
    let quote = '';
    let angle = false;
    for (i = start; i < text.length; i++) {
        if (i - start > 4096 || depth > MAX_DEPTH)
            return null;
        const char = text[i];
        if (char === '\\') {
            i++;
            continue;
        }
        if (quote) {
            if (char === quote)
                quote = '';
            continue;
        }
        if (angle) {
            if (char === '>')
                angle = false;
            continue;
        }
        if (char === '<' && !text.slice(start, i).trim()) {
            angle = true;
            continue;
        }
        if ((char === '"' || char === "'") && /\s/.test(text[i - 1] ?? '')) {
            quote = char;
            continue;
        }
        if (char === '(')
            depth++;
        else if (char === ')' && --depth === 0)
            break;
    }
    if (depth !== 0)
        return null;
    const raw = text.slice(start, i).trim();
    const destination = /^(?:<([^<>\n]*)>|([^\s]+))(?:\s+["'][\s\S]*["'])?$/.exec(raw);
    if (!destination)
        return null;
    return { label, destination: destination[1] ?? destination[2], end: i + 1 };
}
/**
 * Whether `value` is at index `at` of `text`. Never `text.startsWith(value, at)`: a host page can replace
 * String.prototype.startsWith with a version that ignores the position (MicrosoftAjax.js, which Office.js loads in an
 * Outlook task pane, answers `this.substr(0, prefix.length) === prefix`). With that version every index of a text that
 * begins with `**` "starts with" `**`, so the parser took the two characters before and after each word-initial
 * letter for bold markers: "**Start date**" rendered as "**Starde**" and a streamed bulleted list lost letters inside
 * its words (Office pane in Outlook, 2026-10-08 and 2026-10-21).
 */
const startsAt = (text, value, at) => text.slice(at, at + value.length) === value;
function delimiterEnd(text, marker, from) {
    for (let i = from; i < text.length; i++) {
        if (text[i] === '\\') {
            i++;
            continue;
        }
        if (text[i] === '`') {
            let run = i + 1;
            while (text[run] === '`')
                run++;
            const end = codeEnd(text, run, run - i);
            if (end >= 0) {
                i = end + run - i - 1;
                continue;
            }
        }
        if (startsAt(text, marker, i) && !/\s/.test(text[i - 1] ?? '')
            && !(marker.includes('_') && /[\p{L}\p{N}]/u.test(text[i + marker.length] ?? '')))
            return i;
    }
    return -1;
}
/** Small, bounded inline grammar. Incomplete delimiters stay literal while a response is streaming. */
export function parseInlineMarkdown(text, depth = 0, links = true) {
    // Preserve unusually large fragments as text instead of repeatedly scanning unclosed delimiters.
    if (depth >= MAX_DEPTH || text.length > 16_000)
        return [{ type: 'text', text }];
    const result = [];
    let plain = '';
    const flush = () => { if (plain) {
        result.push({ type: 'text', text: plain });
        plain = '';
    } };
    for (let i = 0; i < text.length;) {
        const char = text[i];
        if (char === '\\' && text[i + 1] === '\n') {
            flush();
            result.push({ type: 'break' });
            i += 2;
            continue;
        }
        if (char === '\\' && ESCAPABLE.test(text[i + 1] ?? '')) {
            plain += text[i + 1];
            i += 2;
            continue;
        }
        if (char === '\n') {
            if (/ {2}$/.test(plain)) {
                plain = plain.replace(/ +$/, '');
                flush();
                result.push({ type: 'break' });
            }
            else
                plain += ' ';
            i++;
            continue;
        }
        if (char === '`') {
            let run = i + 1;
            while (text[run] === '`')
                run++;
            const end = codeEnd(text, run, run - i);
            if (end >= 0) {
                flush();
                let code = text.slice(run, end).replace(/\n/g, ' ');
                if (code.startsWith(' ') && code.endsWith(' ') && /[^ ]/.test(code))
                    code = code.slice(1, -1);
                result.push({ type: 'code', text: code });
                i = end + run - i;
                continue;
            }
            plain += text.slice(i, run);
            i = run;
            continue;
        }
        const image = char === '!' && text[i + 1] === '[';
        if ((char === '[' && links) || image) {
            const link = linkAt(text, image ? i + 1 : i);
            if (link) {
                flush();
                const children = parseInlineMarkdown(link.label, depth + 1, false);
                const href = image ? null : safeMarkdownUrl(link.destination);
                if (href)
                    result.push({ type: 'link', href, children });
                else if (image)
                    result.push({ type: 'text', text: link.label ? `[Image: ${link.label}]` : '[Image]' });
                else
                    result.push(...children);
                i = link.end;
                continue;
            }
        }
        if (char === '<' && links) {
            const close = text.indexOf('>', i + 1);
            const href = close >= 0 ? safeMarkdownUrl(text.slice(i + 1, close)) : null;
            if (href) {
                flush();
                result.push({ type: 'link', href, children: [{ type: 'text', text: href }] });
                i = close + 1;
                continue;
            }
        }
        const marker = ['***', '___', '**', '__', '~~', '*', '_'].find(value => startsAt(text, value, i));
        if (marker && !/\s/.test(text[i + marker.length] ?? ' ')
            && !(marker.includes('_') && /[\p{L}\p{N}]/u.test(text[i - 1] ?? ''))) {
            const end = delimiterEnd(text, marker, i + marker.length);
            if (end > i + marker.length) {
                flush();
                const children = parseInlineMarkdown(text.slice(i + marker.length, end), depth + 1, links);
                if (marker.length === 3)
                    result.push({ type: 'strong', children: [{ type: 'em', children }] });
                else
                    result.push({ type: marker === '~~' ? 'strike' : marker.length === 2 ? 'strong' : 'em', children });
                i = end + marker.length;
                continue;
            }
        }
        plain += char;
        i++;
    }
    flush();
    return result;
}
function listMarker(line) {
    const match = /^( *)([-+*]|\d{1,9}[.)]) +(.*)$/.exec(line);
    if (!match)
        return null;
    return { indent: match[1].length, ordered: /^\d/.test(match[2]), start: Number.parseInt(match[2], 10) || 1,
        content: match[3], contentIndent: line.length - match[3].length };
}
function fence(line) { return /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(line); }
function rule(line) { return /^ {0,3}(?:(?:\*\s*){3,}|(?:-\s*){3,}|(?:_\s*){3,})$/.test(line); }
function tableAt(lines, i) {
    const header = splitTableRow(lines[i] ?? '');
    const separator = splitTableRow(lines[i + 1] ?? '');
    if (!header?.length || header.length !== separator?.length || !separator.every(cell => /^:?-{3,}:?$/.test(cell)))
        return null;
    const align = separator.map(cell => cell.startsWith(':') && cell.endsWith(':') ? 'center' : cell.endsWith(':') ? 'right' : cell.startsWith(':') ? 'left' : undefined);
    return { header, align };
}
function blockStart(lines, i) {
    const line = lines[i] ?? '';
    return Boolean(fence(line) || /^ {0,3}#{1,6}(?:\s|$)/.test(line) || /^ {0,3}>/.test(line) || rule(line) || listMarker(line) || tableAt(lines, i));
}
const CALLOUT_WORDS = { warning: 'warning', tip: 'tip', done: 'done', error: 'error' };
const CALLOUT_TITLES = { warning: 'Warning', tip: 'Tip', done: 'Done', error: 'Error' };
const CALLOUT_MARKER = /^(?:\*\*(warning|tip|done|error):\*\*|\*\*(warning|tip|done|error)\*\*:|(warning|tip|done|error):|(⚠)\uFE0F?:?)(?:[ \t]+(.*))?[ \t]*$/i;
/** The callout marker at the start of a paragraph's first line, with the words after it, or null for prose. */
export function calloutMarker(firstLine) {
    const match = CALLOUT_MARKER.exec(firstLine);
    if (!match)
        return null;
    const word = (match[1] ?? match[2] ?? match[3] ?? '').toLowerCase();
    return { kind: match[4] ? 'warning' : CALLOUT_WORDS[word], rest: (match[5] ?? '').trim() };
}
/** `blocks` as a callout when its first block is a marker paragraph (the module comment's grammar), else null. */
function calloutFromBlocks(blocks) {
    const first = blocks[0];
    if (first?.type !== 'paragraph')
        return null;
    const lines = first.text.split('\n');
    const marker = calloutMarker(lines[0]);
    if (!marker)
        return null;
    const body = [];
    let title;
    if (lines.length > 1) {
        title = marker.rest || CALLOUT_TITLES[marker.kind];
        body.push({ type: 'paragraph', text: lines.slice(1).join('\n') });
    }
    else {
        title = CALLOUT_TITLES[marker.kind];
        if (marker.rest)
            body.push({ type: 'paragraph', text: marker.rest });
    }
    return { type: 'callout', kind: marker.kind, title, blocks: [...body, ...blocks.slice(1)] };
}
const STATUS_WORDS = { open: 'open', closed: 'closed', overdue: 'overdue', 'in review': 'review' };
const STATUS_TOKEN = /^(.*?)\s+(?:\*\*(open|closed|overdue|in review)\*\*|\((open|closed|overdue|in review)\)|\[(open|closed|overdue|in review)\]|[·—–]\s+(open|closed|overdue|in review))[ \t]*$/i;
/** A task item's first line without its trailing status token and ` · ` note, and those two when present. */
export function taskLine(line) {
    let text = line;
    let status;
    const token = STATUS_TOKEN.exec(line);
    if (token) {
        text = token[1];
        status = STATUS_WORDS[(token[2] ?? token[3] ?? token[4] ?? token[5]).toLowerCase()];
    }
    const dot = text.lastIndexOf(' · ');
    if (dot > 0 && text.slice(dot + 3).trim()) {
        const note = text.slice(dot + 3).trim();
        return { text: text.slice(0, dot).trimEnd(), ...(status ? { status } : {}), note };
    }
    return { text, ...(status ? { status } : {}) };
}
/** `blocks` of a blockquote with the source line (the module comment's grammar) taken out of its last paragraph. */
function quoteWithSource(blocks) {
    const last = blocks.at(-1);
    if (last?.type !== 'paragraph')
        return { blocks };
    const lines = last.text.split('\n');
    const match = /^(?:—|–|--)\s*(\S.*)$/.exec(lines.at(-1).trim());
    if (!match)
        return { blocks };
    const [name = '', ...details] = match[1].split(/\s+·\s+/).map(part => part.trim()).filter(Boolean);
    const kept = lines.slice(0, -1).join('\n').trim();
    const paragraph = kept ? [{ type: 'paragraph', text: kept }] : [];
    return { blocks: [...blocks.slice(0, -1), ...paragraph], source: { name, details } };
}
const SOURCES_PARAGRAPH = /^\*{0,2}sources:?\*{0,2}:?$/i;
/** The last two blocks of an answer as one `sources` block when they are a "Sources:" line and a list. */
function sourcesRow(blocks) {
    const list = blocks.at(-1);
    const label = blocks.at(-2);
    if (list?.type !== 'list' || !label)
        return;
    const labelled = (label.type === 'paragraph' && SOURCES_PARAGRAPH.test(label.text.trim())) || (label.type === 'heading' && /^sources:?$/i.test(label.text.trim()));
    if (!labelled)
        return;
    const items = list.items.map(item => { const first = item.blocks[0]; return first?.type === 'paragraph' ? first.text.replace(/\s*\n\s*/g, ' ').trim() : ''; });
    blocks.splice(-2, 2, { type: 'sources', items });
}
const TILE_TONES = { good: 'good', green: 'good', bad: 'bad', red: 'bad' };
/** One item of a `Key numbers:` list as a tile (the module comment's grammar), or null when the item is not one. */
export function tileLine(line) {
    const cells = splitTableRow(line);
    if (!cells)
        return null;
    const [value = '', label = '', change = '', tone = ''] = cells;
    if (!value || !label || value.length > 40 || label.length > 80 || cells.length > 4)
        return null;
    if (tone && !TILE_TONES[tone.toLowerCase()])
        return null;
    return { value, label, ...(change ? { change } : {}), ...(tone ? { tone: TILE_TONES[tone.toLowerCase()] } : {}) };
}
const KEY_NUMBERS_LABEL = /^\*{0,2}key numbers\*{0,2}:?\*{0,2}$/i;
const CARDS_LABEL = /^\*{0,2}(?:links|related|related links)\*{0,2}:?\*{0,2}$/i;
/** A list item longer than this is never a tile or a card (it stays a list item); it also keeps the scans below short. */
const MARKER_ITEM_MAX = 2000;
/** `text`'s lines trimmed and joined by single spaces: one pass, unlike a regex over a long run of white space. */
const oneLine = (text) => text.split('\n').map(part => part.trim()).filter(Boolean).join(' ');
/**
 * A list item's text as a link card (the module comment's grammar), or null when it is not one: a leading link gives the
 * name and target, ` · ` (or ` — `) the detail; without a leading link, a ` · ` segment after the first must name an app.
 */
export function cardItem(text) {
    const flat = oneLine(text);
    const link = flat[0] === '[' ? linkAt(flat, 0) : null;
    if (link) {
        const href = safeMarkdownUrl(link.destination) ?? undefined;
        let rest = flat.slice(link.end).trimStart();
        if (rest[0] === '·' || rest[0] === '—')
            rest = rest.slice(1).trimStart();
        const detail = rest.trimEnd();
        return { name: link.label.trim() || href || flat, ...(href ? { href } : {}), ...(detail ? { detail } : {}), text: flat };
    }
    const segments = dotSegments(flat);
    const at = segments.findIndex((segment, index) => index > 0 && linkCardApp(undefined, segment) !== 'generic');
    if (at < 1)
        return null;
    return { name: segments.slice(0, at).join(' · '), detail: segments.slice(at).join(' · '), text: flat };
}
/**
 * A list item's words for a marker: one paragraph (or, `heading` set, one heading, which is what a lone "-" streamed under
 * an item makes of it), no check box, at most 2,000 characters; else null.
 */
function itemWords(item, heading) {
    const [first, ...rest] = item.blocks;
    if (item.checked !== undefined || rest.length || !first)
        return null;
    if (first.type !== 'paragraph' && !(heading && first.type === 'heading'))
        return null;
    return first.text.length > MARKER_ITEM_MAX ? null : oneLine(first.text);
}
/**
 * Each "Key numbers:" line followed by a list of tiles as one `tiles` block, and each "Links:" line followed by a list of
 * cards as one `cards` block, at any depth, in `blocks` (in place); a list with any other item stays as written.
 * `answerEnd` is set for the answer's own blocks: there, the last item of a list that ends the answer may be in any shape
 * (a streaming reply's next item arriving); while it is not a tile or a card it stays a list item after the row instead of
 * turning the whole row back into a list.
 */
function markerLists(blocks, answerEnd) {
    for (let i = 0; i + 1 < blocks.length; i++) {
        const label = blocks[i];
        const list = blocks[i + 1];
        if ((label.type !== 'paragraph' && label.type !== 'heading') || list.type !== 'list')
            continue;
        const words = label.text.trim();
        const cards = CARDS_LABEL.test(words);
        if (!cards && !KEY_NUMBERS_LABEL.test(words))
            continue;
        const ending = answerEnd && i + 2 === blocks.length;
        const last = list.items.length - 1;
        const parsed = list.items.map((item, index) => {
            const text = itemWords(item, ending && index === last);
            return text === null ? null : cards ? cardItem(text) : tileLine(text);
        });
        // The answer's last item, still arriving; as the list's only item it must look like the start of one.
        const lastFirst = list.items[last].blocks[0];
        const lastText = !lastFirst ? '' : lastFirst.type === 'paragraph' || lastFirst.type === 'heading' ? lastFirst.text.trim() : null;
        const begins = last > 0 || lastText === '' || (lastText !== null && (cards ? lastText[0] === '[' : lastText.includes('|')));
        const arriving = ending && !parsed[last] && begins;
        const kept = arriving ? parsed.slice(0, last) : parsed;
        if (!kept.every(Boolean) || (!cards && kept.length > 12))
            continue;
        const row = [cards ? { type: 'cards', label: words, items: kept } : { type: 'tiles', label: words, tiles: kept }];
        if (arriving)
            row.push({ ...list, start: list.start + last, items: [list.items[last]] });
        blocks.splice(i, 2, ...row);
    }
    // A marker label ending the answer, with its list not yet arrived (a lone "-" under it reads as a setext underline).
    const tail = blocks.at(-1);
    if (answerEnd && (tail?.type === 'paragraph' || tail?.type === 'heading')) {
        const words = tail.text.trim();
        if (CARDS_LABEL.test(words))
            blocks.splice(-1, 1, { type: 'cards', label: words, items: [] });
        else if (KEY_NUMBERS_LABEL.test(words))
            blocks.splice(-1, 1, { type: 'tiles', label: words, tiles: [] });
    }
}
const NUMERIC_CELL = /^[+\-−–]?\(?\s*[$€£¥]?\s*[+\-−–]?\d[\d,]*(?:\.\d+)?\s*(?:%|[kKMB])?\s*\)?$/;
/** A table cell's kind: a number (plain, currency, percentage; signed or in accounting parentheses) or text (null). */
export function tableCellKind(cell) {
    const plain = inlinePlainText(parseInlineMarkdown(cell)).trim();
    if (!plain || !NUMERIC_CELL.test(plain))
        return null;
    if (plain.startsWith('+'))
        return 'positive';
    if (/^[\-−–]/.test(plain) || (plain.startsWith('(') && plain.endsWith(')')))
        return 'negative';
    return 'number';
}
/** Each column's alignment: the Markdown alignment when set, else right when every body cell holding anything is numeric. */
export function tableColumnAlignment(block) {
    return block.header.map((_, column) => {
        if (block.align[column])
            return block.align[column];
        const cells = block.rows.map(row => row[column] ?? '').filter(cell => cell.trim());
        return cells.length && cells.every(cell => tableCellKind(cell)) ? 'right' : undefined;
    });
}
/** Whether a body row is the table's total line: its first cell starts with the word Total. */
export function isTotalRow(row) {
    return /^total\b/i.test(inlinePlainText(parseInlineMarkdown(row[0] ?? '')).trim());
}
/** The table as tab-separated text (header first), each cell its plain words with tabs and line breaks made spaces. */
export function tableTsv(block) {
    const cell = (text) => inlinePlainText(parseInlineMarkdown(text)).replace(/[\t\r\n]+/g, ' ').trim();
    return [block.header, ...block.rows].map(row => row.map(cell).join('\t')).join('\n');
}
/** Render-focused Markdown subset: no raw HTML, extensions with side effects, or remote images. */
export function parseChatMarkdown(text, depth = 0) {
    if (depth >= MAX_DEPTH)
        return [{ type: 'paragraph', text }];
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    const blocks = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        if (!line.trim()) {
            i++;
            continue;
        }
        const opening = fence(line);
        if (opening) {
            const marker = opening[1];
            const language = opening[2].trim().split(/\s+/)[0]?.slice(0, 60) ?? '';
            const code = [];
            const closing = new RegExp(`^ {0,3}${marker[0]}{${marker.length},}\\s*$`);
            i++;
            while (i < lines.length && !closing.test(lines[i]))
                code.push(lines[i++]);
            const complete = i < lines.length;
            if (complete)
                i++;
            blocks.push({ type: 'code', language, text: code.join('\n'), complete });
            continue;
        }
        const heading = /^ {0,3}(#{1,6})(?:[ \t]+(.*)|$)/.exec(line);
        if (heading) {
            blocks.push({ type: 'heading', level: heading[1].length, text: (heading[2] ?? '').replace(/[ \t]+#+[ \t]*$/, '').trim() });
            i++;
            continue;
        }
        const table = tableAt(lines, i);
        if (table) {
            i += 2;
            const rows = [];
            while (i < lines.length && lines[i].trim()) {
                const row = splitTableRow(lines[i]);
                if (!row)
                    break;
                // Keep extra cells visible instead of discarding a model's streamed text.
                if (row.length > table.header.length)
                    row.splice(table.header.length - 1, row.length, row.slice(table.header.length - 1).join(' | '));
                while (row.length < table.header.length)
                    row.push('');
                rows.push(row);
                i++;
            }
            blocks.push({ type: 'table', ...table, rows });
            continue;
        }
        if (rule(line)) {
            blocks.push({ type: 'rule' });
            i++;
            continue;
        }
        if (/^ {0,3}>/.test(line)) {
            const quote = [];
            while (i < lines.length && /^ {0,3}>/.test(lines[i]))
                quote.push(lines[i++].replace(/^ {0,3}> ?/, ''));
            const inner = parseChatMarkdown(quote.join('\n'), depth + 1);
            const first = inner[0];
            blocks.push(first?.type === 'callout' ? { ...first, blocks: [...first.blocks, ...inner.slice(1)] } : { type: 'quote', ...quoteWithSource(inner) });
            continue;
        }
        const marker = listMarker(line);
        if (marker) {
            const items = [];
            while (i < lines.length) {
                const next = listMarker(lines[i]);
                if (!next || next.indent !== marker.indent || next.ordered !== marker.ordered)
                    break;
                const task = /^\[([ xX])\] +(.*)$/.exec(next.content);
                const taskText = task ? taskLine(task[2]) : undefined;
                const content = [taskText ? taskText.text : next.content];
                i++;
                let blank = false;
                while (i < lines.length) {
                    const current = lines[i];
                    const child = listMarker(current);
                    if (child && child.indent <= marker.indent)
                        break;
                    if (!current.trim()) {
                        let following = i + 1;
                        while (following < lines.length && !lines[following].trim())
                            following++;
                        const after = lines[following] ?? '';
                        if (/^ */.exec(after)[0].length <= marker.indent)
                            break;
                        content.push('');
                        blank = true;
                        i++;
                        continue;
                    }
                    const indent = /^ */.exec(current)[0].length;
                    if (indent > marker.indent) {
                        content.push(current.slice(Math.min(next.contentIndent, indent)));
                        i++;
                        continue;
                    }
                    if (blank || blockStart(lines, i))
                        break;
                    content.push(current);
                    i++;
                }
                items.push({ ...(task ? { checked: task[1].toLowerCase() === 'x' } : {}), ...(taskText?.status ? { status: taskText.status } : {}), ...(taskText?.note ? { note: taskText.note } : {}), blocks: parseChatMarkdown(content.join('\n'), depth + 1) });
                let following = i;
                while (following < lines.length && !lines[following].trim())
                    following++;
                const sibling = listMarker(lines[following] ?? '');
                if (sibling && sibling.indent === marker.indent && sibling.ordered === marker.ordered)
                    i = following;
                else
                    break;
            }
            blocks.push({ type: 'list', ordered: marker.ordered, start: marker.start, items });
            continue;
        }
        if (/^ {0,3}(?:=+|-+)\s*$/.test(lines[i + 1] ?? '')) {
            blocks.push({ type: 'heading', level: lines[i + 1].trim().startsWith('=') ? 1 : 2, text: line.trim() });
            i += 2;
            continue;
        }
        const paragraph = [line];
        i++;
        while (i < lines.length && lines[i].trim() && !blockStart(lines, i)) {
            // A following setext delimiter belongs to its preceding line, not this paragraph.
            if (/^ {0,3}(?:=+|-+)\s*$/.test(lines[i + 1] ?? ''))
                break;
            paragraph.push(lines[i++]);
        }
        const paragraphBlock = { type: 'paragraph', text: paragraph.join('\n') };
        blocks.push(calloutFromBlocks([paragraphBlock]) ?? paragraphBlock);
    }
    markerLists(blocks, depth === 0);
    if (depth === 0)
        sourcesRow(blocks);
    return blocks;
}
/**
 * The code blocks of parsed Markdown in reading order, nested ones (in lists and quotes) included: a block's position in
 * this list, from 1, is the number its download is named by and its web page preview is known by.
 */
export function codeBlocksInOrder(blocks) {
    const found = [];
    const walk = (list) => {
        for (const block of list) {
            if (block.type === 'code')
                found.push(block);
            else if (block.type === 'quote' || block.type === 'callout')
                walk(block.blocks);
            else if (block.type === 'list')
                for (const item of block.items)
                    walk(item.blocks);
        }
    };
    walk(blocks);
    return found;
}
function inlinePlainText(nodes) {
    return nodes.map((node) => node.type === 'text' || node.type === 'code' ? node.text : node.type === 'break' ? ' ' : inlinePlainText(node.children)).join('');
}
function blocksPlainText(blocks) {
    const lines = [];
    for (const block of blocks) {
        if (block.type === 'paragraph' || block.type === 'heading')
            lines.push(inlinePlainText(parseInlineMarkdown(block.text)));
        else if (block.type === 'code')
            lines.push(block.text);
        else if (block.type === 'quote') {
            lines.push(...blocksPlainText(block.blocks));
            if (block.source)
                lines.push([block.source.name, ...block.source.details].map((part) => inlinePlainText(parseInlineMarkdown(part))).join(', '));
        }
        else if (block.type === 'callout')
            lines.push(inlinePlainText(parseInlineMarkdown(block.title)), ...blocksPlainText(block.blocks));
        else if (block.type === 'sources')
            lines.push(['Sources', ...block.items.map((item) => inlinePlainText(parseInlineMarkdown(item)))].join(', '));
        else if (block.type === 'tiles')
            for (const tile of block.tiles) {
                const words = (part) => inlinePlainText(parseInlineMarkdown(part));
                lines.push(`${words(tile.value)} ${words(tile.label)}${tile.change ? `, ${words(tile.change)}` : ''}.`);
            }
        else if (block.type === 'cards')
            for (const item of block.items)
                lines.push([item.name, ...(item.detail ? [item.detail] : [])].map((part) => inlinePlainText(parseInlineMarkdown(part))).join(', '));
        else if (block.type === 'list')
            for (const item of block.items) {
                lines.push(...blocksPlainText(item.blocks));
                if (item.note)
                    lines.push(item.note);
                if (item.status)
                    lines.push(item.status === 'review' ? 'In review' : item.status[0].toUpperCase() + item.status.slice(1));
            }
        else if (block.type === 'table')
            for (const row of [block.header, ...block.rows])
                lines.push(row.map((cell) => inlinePlainText(parseInlineMarkdown(cell))).filter(Boolean).join(', '));
    }
    return lines;
}
/**
 * The words of a Markdown answer as plain text, for a screen reader's announcement of it: no emphasis or heading markers,
 * no list bullets, no link targets or code fences, table cells read as a list. Whitespace is collapsed to single spaces.
 */
export function markdownPlainText(text) {
    return blocksPlainText(parseChatMarkdown(text)).map((line) => line.replace(/\s+/g, ' ').trim()).filter(Boolean).join(' ');
}
/** `text` cut to at most `max` characters at a word boundary, with an ellipsis when anything was cut. */
export function clipAtWord(text, max) {
    if (text.length <= max)
        return text;
    const cut = text.slice(0, Math.max(0, max - 1));
    // The cut ends a word when the next character is a space; else it goes back to the last space, unless that loses too much.
    const boundary = /\s/.test(text.charAt(cut.length)) ? cut.length : cut.lastIndexOf(' ');
    return `${(boundary > max * 0.6 ? cut.slice(0, boundary) : cut).replace(/[\s,;:.]+$/, '')}…`;
}
//# sourceMappingURL=chatMarkdownParser.js.map